import { capitalize, uniq } from 'lodash';

import {
  DELAY_STATUS,
  ENTIRE_AVAILABLE,
  FILTER_BODYTYPE_NARROW,
  FILTER_BODYTYPE_WIDE,
  FILTER_HAULING_FREIGHT,
  MODE_AIR,
  MODE_WATER,
  NONE_AVAILABLE,
  PARTIAL_AVAILABLE,
  VEHICLE_TYPES,
} from '@/lib/constants';
import {
  OptionItem,
  RouteVehicleCoverage,
  VehicleDelayInterface,
  VehicleFilterAttributes,
  VehicleSearchInterface,
} from '@/types';
import {
  FeatureSetApiV2Interface,
  RouteVehicleApiV2Interface,
  ScheduleRouteSegmentApiInterface,
  SegmentModeType,
} from '@/types/api-types';
import { getDayDifferenceBetweenDates } from '@/utils/format/date-time';

// Returns vehicle Ids from Schedule Segments
export const getVehicleIdsFromScheduleSegment = (segments: ScheduleRouteSegmentApiInterface[]) => {
  const ids = segments.map((segment) => segment.vehicleId);
  const filteredIds = ids.filter((id) => id !== 'EmptyVehicle');
  return uniq(filteredIds);
};

/**
 * Returns Vessel, Aircraft or vehicle based on mode type
 * @param mode SegmentModeType
 * @returns Vessel | Aircraft | Vehicle
 */
export const getVehicleType = (mode: SegmentModeType): string => {
  switch (mode) {
    case MODE_AIR:
      return capitalize(VEHICLE_TYPES.AIRCRAFT);
    case MODE_WATER:
      return capitalize(VEHICLE_TYPES.VESSEL);
    default:
      return 'Vehicle';
  }
};

// Get a cleaned up vehicle name
export const getVehicleName = (vehicle: RouteVehicleApiV2Interface): string => {
  if (vehicle?.names?.short?.length) {
    const name: string = vehicle.names.short[0];
    return name.split(' Passenger')[0];
  }
  return '';
};

/**
 * Get Vehicle Mode (Air, Water, Rail, etc)
 * @param vehicle API Vehicle object
 * @returns SegmentModeType
 */
export const getVehicleModeType = (vehicle: RouteVehicleApiV2Interface): SegmentModeType => {
  return vehicle.type;
};

// Gets a basic vehicle description (if Aircraft)
export const getVehicleDescription = (featureSet: FeatureSetApiV2Interface): string => {
  if (featureSet?.airplane) {
    const fuselage = getVehicleFuselage(featureSet);
    const propulsion = capitalize(featureSet.airplane.propulsion.join(','));
    return `${fuselage} ${propulsion}`;
  }
  return '';
};

// Gets a basic vehicle fusalage type (if Aircraft)
export const getVehicleFuselage = (featureSet: FeatureSetApiV2Interface): string => {
  if (featureSet?.airplane) {
    return capitalize(featureSet.airplane.fuselage.join(','));
  }
  return '';
};

// Gets the type of cargo the vehicle hauls (passengers or freight)
export const getVehicleHauling = (vehicle: RouteVehicleApiV2Interface): string => {
  if (!vehicle) return '';

  // if vehicle is a freighte aircraft
  if (isFreighterAircraft(vehicle)) {
    return capitalize(FILTER_HAULING_FREIGHT);
  }

  // Else return joined string of hauling
  const vehicleHauling = vehicle.featureSet.vehicle?.hauling || [];
  return capitalize(vehicleHauling.length > 0 ? vehicleHauling.join(', ') : 'unknown');
};

// Gets a vehicle TEU (typically a Ocean vehicle)
export const getVehicleTeu = (featureSet: FeatureSetApiV2Interface): string => {
  const teu = featureSet?.oceanLiner?.teu;
  return teu ? `${teu.toLocaleString()} TEU` : '';
};

/**
 * Has Freight only vehicle in array
 * @param vehicles Array of Vehicles
 * @returns Returns true of false if an array of vehicles contains a freighter
 */
export const hasFreightOnlyVehicles = (vehicles: RouteVehicleApiV2Interface[]): boolean => {
  return vehicles.reduce((hasFreighter, vehicle) => {
    // Early out if freighter is already identified
    if (hasFreighter) return true;
    // Else check if it is a freighter aircraft and return
    return isFreighterAircraft(vehicle);
  }, false);
};

/**
 * Determines if a vehicle is a freighter aircraft
 */
export const isFreighterAircraft = (vehicle: RouteVehicleApiV2Interface): boolean => {
  if (!vehicle) return false;

  const vehicleName = getVehicleName(vehicle)?.toLowerCase() || '';
  const vehicleHauling = vehicle.featureSet.vehicle?.hauling || [];
  const vehicleIsAircraft = !!vehicle.featureSet?.airplane;

  // If is an aircraft and either the name mentions freighter or the "hauling" prop includes freight
  if (
    (vehicleIsAircraft && vehicleName.includes(FILTER_HAULING_FREIGHT)) ||
    vehicleHauling.includes(FILTER_HAULING_FREIGHT)
  ) {
    return true;
  }

  return false;
};

// Returns if a vehicle is an RFS truck
export const isRfsVehicle = (vehicle: RouteVehicleApiV2Interface): boolean => {
  return !!vehicle.featureSet?.truck?.isRFS;
};

// Gets RFS payload type
export const getRfsVehiclePayload = (vehicle: RouteVehicleApiV2Interface): string => {
  return capitalize((vehicle.featureSet?.truck?.supportedPayload || []).join(', '));
};

/**
 * Get Vehicle Delay
 * @param vehicle - Vehicle Object
 * @returns Delay in Days
 *
 * @remarks
 * -1 ahead of schedule, 0 appears on time, > 0 delayed
 */
export const getVehicleDelay = (
  vehicle: RouteVehicleApiV2Interface,
  departureDateTime: string,
): VehicleDelayInterface | undefined => {
  if (!vehicle?.latestPos) return undefined;

  const delay = vehicle?.latestPos?.delay;
  const delayInDays = delay / 60 / 60 / 24;
  const roundedDelayInDays = Math.round(delayInDays);

  // If less than 0, assume is ahead of schedule and return -1
  if (delayInDays < -1) {
    return { status: DELAY_STATUS.AHEAD, days: roundedDelayInDays };
  }

  // Is roughly on time, return 0
  if (delayInDays < 1 && delayInDays > -1) {
    return { status: DELAY_STATUS.ONTIME, days: roundedDelayInDays };
  }

  // Determine date offset from today to determine how fresh Delay is
  const today = new Date();
  const timeUntil = getDayDifferenceBetweenDates(today, departureDateTime);

  // Significance determines the ratio of time until vs the delay amount
  // if its a small significance, ie two weeks away and a 1 day delay we may not show the delay to the user
  const significance = delayInDays / timeUntil;
  // If less than a week, return all delays
  if (timeUntil <= 7) {
    return { status: DELAY_STATUS.DELAYED, days: roundedDelayInDays };
  }
  // else return based on significance
  if (significance > 0.2) {
    return { status: DELAY_STATUS.DELAYED, days: roundedDelayInDays };
  }

  // Else return 'ontime'
  return { status: DELAY_STATUS.ONTIME, days: roundedDelayInDays };
};

// Returns object containing vehicle attributes and if they exist
export const getVehicleFilterAttributes = (vehicle: RouteVehicleApiV2Interface): VehicleFilterAttributes => {
  const vehicleFeatureSet = vehicle && vehicle.featureSet;

  // Handle aircraft
  if (vehicleFeatureSet?.airplane) {
    const vehicleHauling = getVehicleHauling(vehicle)?.toLowerCase() || '';
    const fuselage = getVehicleFuselage(vehicle.featureSet)?.toLowerCase() || '';
    // Set attributes
    const isWidebody = fuselage.includes(FILTER_BODYTYPE_WIDE);
    const isNarrowbody = fuselage.includes(FILTER_BODYTYPE_NARROW);
    const isFreighter = vehicleHauling.includes(FILTER_HAULING_FREIGHT);

    return {
      isWidebody,
      isNarrowbody,
      isFreighter,
    };
  }

  // For now, the default returns isFreighter true
  return {
    isWidebody: false,
    isNarrowbody: false,
    isFreighter: true,
  };
};

// Determines if a vehicle attribute has entire, partial or no coverage
export const determineVehicleCoverage = (
  attributes: VehicleFilterAttributes[],
): {
  hasFreightOnlyVehicles: RouteVehicleCoverage;
  widebody: RouteVehicleCoverage;
  narrowbody: RouteVehicleCoverage;
} => {
  return {
    hasFreightOnlyVehicles: getVehicleCoverageAttribute(attributes.map(({ isFreighter }) => isFreighter)),
    widebody: getVehicleCoverageAttribute(attributes.map(({ isWidebody }) => isWidebody)),
    narrowbody: getVehicleCoverageAttribute(attributes.map(({ isNarrowbody }) => isNarrowbody)),
  };
};

// Returns if an array of Booleans contains all false/true/none
export const getVehicleCoverageAttribute = (arr: boolean[]) => {
  if (!arr.includes(true)) return NONE_AVAILABLE;
  if (!arr.includes(false)) return ENTIRE_AVAILABLE;
  return PARTIAL_AVAILABLE;
};

/**
 * Transform VehicleSearch into OptionItems
 */
export const transformVehicleToOptions = (vehicleItems: VehicleSearchInterface[]): OptionItem[] => {
  return vehicleItems.map((item) => {
    return { value: item.vehicleId, label: item.name };
  });
};
